import React, { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { AuthSessionApi } from '../services/api/AuthSessionApi';
import { UserService } from '../services/application/UserService';
import { useNavigate } from 'react-router-dom';
import type { Session } from '@supabase/supabase-js';
import { Logger } from '../utils/logger';
import { toError } from '../utils/errorMessage';
import { getErrorMessage } from '../utils/errorMessage';
import { SingaporeTime } from '../utils/unified-date';

// Define the User type
type AuthUser = {
  id: string;
  email?: string;
  name: string;
  role: 'manager' | 'worker' | 'seller' | 'driver';
};

// Define context type
type AuthContextType = {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  session: Session | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  signUp: (
    email: string,
    password: string,
    name: string,
    role: 'manager' | 'worker' | 'seller' | 'driver'
  ) => Promise<void>;
};

// Create the context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Local storage keys
const STORAGE_KEYS = {
  USER: 'bossy-auth-user',
  SESSION: 'bossy-auth-session',
  LAST_REFRESH: 'bossy-auth-last-refresh',
} as const;

// Session cache duration (1 year)
const SESSION_CACHE_DURATION = 365 * 24 * 60 * 60 * 1000;

// Helper functions for localStorage
const storage = {
  getUser: (): AuthUser | null => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.USER);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  },

  setUser: (user: AuthUser | null) => {
    try {
      if (user) {
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
      } else {
        localStorage.removeItem(STORAGE_KEYS.USER);
      }
    } catch {
      // Ignore storage errors
    }
  },

  getSession: (): Session | null => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.SESSION);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  },

  setSession: (session: Session | null) => {
    try {
      if (session) {
        localStorage.setItem(STORAGE_KEYS.SESSION, JSON.stringify(session));
        localStorage.setItem(STORAGE_KEYS.LAST_REFRESH, SingaporeTime.now());
      } else {
        localStorage.removeItem(STORAGE_KEYS.SESSION);
        localStorage.removeItem(STORAGE_KEYS.LAST_REFRESH);
      }
    } catch {
      // Ignore storage errors
    }
  },

  isSessionCacheValid: (): boolean => {
    try {
      const lastRefresh = localStorage.getItem(STORAGE_KEYS.LAST_REFRESH);
      if (!lastRefresh) return false;

      // The value must be a real wall-clock timestamp. Legacy entries held
      // performance.now() elapsed-ms, which restarts at 0 on every reload and
      // made the cache appear fresh forever; anything unparseable (including
      // those legacy numbers) is invalid so the cache refreshes once and is
      // rewritten in the new format.
      if (!SingaporeTime.isValidSingaporeTimestamp(lastRefresh)) return false;

      const storedMs = SingaporeTime.toMilliseconds(lastRefresh);
      if (!Number.isFinite(storedMs)) return false;

      const ageMs = SingaporeTime.toMilliseconds(SingaporeTime.now()) - storedMs;
      // A future timestamp (e.g. after a clock change) is invalid, not "0s old".
      if (ageMs < 0) return false;

      return ageMs < SESSION_CACHE_DURATION;
    } catch {
      return false;
    }
  },

  clearAll: () => {
    try {
      Object.values(STORAGE_KEYS).forEach(key => {
        localStorage.removeItem(key);
      });
    } catch {
      // Ignore storage errors
    }
  },
};

// Profile fetching with caching
const fetchUserProfile = async (userId: string): Promise<AuthUser | null> => {
  try {
    const { data: profile, error } = await UserService.getProfile(userId);

    if (error || !profile) {
      throw new Error('Profile not found');
    }

    return {
      id: userId,
      email: profile.email,
      name: profile.name,
      role: profile.role as AuthUser['role'],
    };
  } catch (error) {
    Logger.serviceError('AuthContext', 'fetchUserProfile', error as Error, {
      operation: 'fetch_user_profile_failed',
    });
    return null;
  }
};

// Create fallback user profile
const createFallbackUser = (session: Session): AuthUser => {
  const email = session.user.email || '';
  const name = email.split('@')[0] || 'User';

  return {
    id: session.user.id,
    email,
    name,
    // Least privilege: without a profile row the role is unknown, so never
    // assume a privileged one.
    role: 'worker',
  };
};

// Error carrying a message already written for the user; signUp re-throws
// these unchanged instead of wrapping them as database errors.
class UserFacingError extends Error {}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const isInitializedRef = useRef<boolean>(false);
  const navigate = useNavigate();

  // Initialize auth state from localStorage or Supabase
  useEffect(() => {
    let isMounted = true;

    const initializeAuth = async () => {
      try {
        // Prevent multiple initializations
        if (isInitializedRef.current) {
          return;
        }

        // Set ref immediately to prevent race conditions
        isInitializedRef.current = true;

        // Check if we have a cached session first (for persistence)
        const cachedSession = storage.getSession();
        const sessionIsValid = cachedSession && storage.isSessionCacheValid();

        // Preserve cached user profile so role-based UI remains correct between reloads
        // (previously cleared, which caused fallback role to default to 'manager').

        // If we have a valid cached session, use it but also try to set it in Supabase client
        if (sessionIsValid && cachedSession) {
          // Try to set the session in Supabase client so API calls work
          try {
            await AuthSessionApi.setSession({
              access_token: cachedSession.access_token,
              refresh_token: cachedSession.refresh_token,
            });
          } catch (setSessionError) {
            Logger.warn('Failed to set session in Supabase client, API calls may fail:', {
              error: toError(setSessionError),
            });
          }

          setSession(cachedSession);

          // Prefer cached user (with correct role). If missing, try DB; avoid unsafe manager fallback.
          const cachedUser = storage.getUser();
          if (cachedUser && cachedUser.id === cachedSession.user.id) {
            setUser(cachedUser);
            setLoading(false);
            if (window.location.pathname === '/auth/login' || window.location.pathname === '/') {
              navigate(`/${cachedUser.role}/dashboard`);
            }
            return;
          }

          // Fetch profile to resolve role accurately
          try {
            const profile = await fetchUserProfile(cachedSession.user.id);
            if (profile) {
              storage.setUser(profile);
              setUser(profile);
              setLoading(false);
              if (window.location.pathname === '/auth/login' || window.location.pathname === '/') {
                navigate(`/${profile.role}/dashboard`);
              }
              return;
            }
          } catch {
            // ignore, fall through
          }

          // As a last resort, don't assume a privileged role; keep user null so UI stays minimal
          setUser(null);
          setLoading(false);
          return;
        }

        // No valid cache, try to get fresh session from Supabase
        let freshSession = null;
        let sessionError = null;

        try {
          // Add timeout to prevent hanging on CORS errors
          // The Supabase client keeps no session of its own (persistSession is off),
          // so a stale cache must be re-validated with its own tokens: setSession
          // refreshes an expired access token and returns the live session. Asking
          // getSession() here would find nothing and sign everybody out on reload.
          const sessionPromise = cachedSession
            ? AuthSessionApi.setSession({
                access_token: cachedSession.access_token,
                refresh_token: cachedSession.refresh_token,
              })
            : AuthSessionApi.getSession();
          const timeoutPromise = new Promise<never>((_, reject) =>
            setTimeout(
              () => reject(new Error('Supabase getSession timeout - likely CORS issue')),
              5000
            )
          );

          const result = await Promise.race([sessionPromise, timeoutPromise]);
          freshSession = result.data?.session;
          sessionError = result.error;
        } catch (timeoutError) {
          sessionError = timeoutError;
        }

        if (!isMounted) return;

        // The server rejected the cached tokens (revoked or expired refresh token):
        // that is a real sign-out, not a connection problem.
        const rejectedStatus =
          sessionError && typeof sessionError === 'object' && 'status' in sessionError
            ? Number((sessionError as { status?: unknown }).status)
            : NaN;
        if (cachedSession && rejectedStatus >= 400 && rejectedStatus < 500) {
          storage.clearAll();
          setLoading(false);
          return;
        }

        if (sessionError) {
          // Don't log CORS errors as they're expected when Supabase is down
          const errorMessage =
            sessionError instanceof Error ? sessionError.message : String(sessionError);
          if (!errorMessage.includes('CORS') && !errorMessage.includes('timeout')) {
            Logger.serviceError('AuthContext', 'checkAuthState', new Error(errorMessage), {
              operation: 'session_error',
              error: errorMessage,
            });
          }

          setError('Unable to connect to authentication service');
          setLoading(false);
          return;
        }

        if (!freshSession) {
          // No session - user not logged in, clear everything
          storage.clearAll();
          setLoading(false);
          return;
        }

        // We have a session, update state and store session for persistence
        setSession(freshSession);
        storage.setSession(freshSession);

        // Try to get user profile
        const userProfile = await fetchUserProfile(freshSession.user.id);
        const finalUser = userProfile || createFallbackUser(freshSession);

        setUser(finalUser);
        // Cache a real profile only; the fallback user's role is a guess and
        // would be trusted by the cached-user fast path on the next reload.
        if (userProfile) {
          storage.setUser(finalUser);
        }
      } catch (error) {
        Logger.serviceError('AuthContext', 'checkAuthState', error as Error, {
          operation: 'auth_initialization_error',
        });
        if (isMounted) {
          setError('Failed to initialize authentication');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    // Set up auth state change listener
    const {
      data: { subscription },
    } = AuthSessionApi.onAuthStateChange(async (event, session) => {
      if (!isMounted) return;

      // Only handle SIGNED_OUT and TOKEN_REFRESHED events
      // Login is handled directly in the login function
      if (event === 'SIGNED_OUT') {
        setUser(null);
        setSession(null);
        storage.clearAll();
        setLoading(false);
      } else if (event === 'TOKEN_REFRESHED' && session) {
        // Update session and store for persistence
        setSession(session);
        storage.setSession(session);
      }
      // Ignore SIGNED_IN events as login handles this directly
      // Ignore INITIAL_SESSION events as initialization handles this
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [navigate]);

  // Login function
  const login = async (email: string, password: string) => {
    // Check if we already have a valid session and user
    if (session && user) {
      navigate(`/${user.role}/dashboard`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Add timeout to handle CORS errors
      const loginPromise = AuthSessionApi.signInWithPassword({
        email,
        password,
      });

      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new Error('Login timeout - Supabase may be down (CORS/522 error)')),
          10000
        )
      );

      let data, error;
      try {
        const result = await Promise.race([loginPromise, timeoutPromise]);
        data = result.data;
        error = result.error;
      } catch (timeoutError) {
        throw new Error('Unable to connect to authentication service. Please try again later.');
      }

      if (error) {
        Logger.error('Supabase auth error', {
          message: error.message,
          status: error.status,
          code: error.code,
          email,
        });

        // Provide more specific error messages
        if (error.message.includes('Invalid login credentials')) {
          throw new Error(
            'Invalid email or password. Please check your credentials and try again.'
          );
        } else if (error.message.includes('Email not confirmed')) {
          throw new Error('Please confirm your email address before logging in.');
        } else if (error.status === 400) {
          throw new Error('Invalid login request. Please check your email and password format.');
        } else {
          throw error;
        }
      }

      if (!data.session) {
        throw new Error('No session returned from login');
      }

      // Removed verbose logging - was causing too much noise
      // Logger.debug('Login successful', { userId: data.session.user.id });

      // Don't rely on onAuthStateChange, handle navigation directly for login
      const userProfile = await fetchUserProfile(data.session.user.id);
      const finalUser = userProfile || createFallbackUser(data.session);

      // Update state directly
      setSession(data.session);
      storage.setSession(data.session);
      setUser(finalUser);
      // Cache a real profile so the cached-user fast path works after a
      // reload; never the fallback user, whose guessed role would be trusted.
      if (userProfile) {
        storage.setUser(finalUser);
      }
      setLoading(false);

      // Navigate to appropriate dashboard
      navigate(`/${finalUser.role}/dashboard`);
    } catch (error: unknown) {
      Logger.serviceError('AuthContext', 'login', error as Error, {
        email,
        operation: 'login_error',
        errorDetails:
          error && typeof error === 'object' && 'status' in error && error.status
            ? { status: error.status, code: (error as { code?: unknown }).code }
            : undefined,
      });
      setError(getErrorMessage(error, 'Login failed'));
      setLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      const { error } = await AuthSessionApi.signOut();

      if (error) {
        Logger.serviceError('AuthContext', 'logout', new Error(error.message), {
          operation: 'logout_supabase_error',
          error,
        });
      }

      // Clear React Query cache on logout
      const { queryClient } = await import('../query/QueryClientProvider');
      queryClient.clear();

      // Clear everything regardless of API result
      setUser(null);
      setSession(null);
      storage.clearAll();
      navigate('/auth/login');
    } catch (error) {
      Logger.serviceError('AuthContext', 'logout', error as Error, {
        operation: 'logout_catch_error',
      });
      // Still clear local state even if API fails
      setUser(null);
      setSession(null);
      storage.clearAll();
      navigate('/auth/login');
    }
  };

  // Sign up function
  const signUp = async (
    email: string,
    password: string,
    name: string,
    role: 'manager' | 'worker' | 'seller' | 'driver' = 'worker'
  ) => {
    setLoading(true);
    setError(null);

    try {
      // Create auth user without role in metadata to avoid validation issues
      // We'll store the role directly in the database
      const { data, error } = await AuthSessionApi.signUp(email, password, name);

      if (error) {
        throw error;
      }

      if (!data.user) {
        throw new Error('Signup failed - no user returned');
      }

      // Try to create user profile with the actual role
      try {
        // First check if user already exists by ID
        const { data: existingUser } = await UserService.findById(data.user.id);

        if (existingUser) {
          // User already exists, update it instead
          const { error: updateError } = await UserService.updateProfile(data.user.id, {
            name,
            email,
            role,
          });

          if (updateError) {
            // Check if it's a duplicate name error
            if (
              updateError.message.includes('duplicate key') &&
              (updateError.message.includes('username_unique') ||
                updateError.message.includes('users_name_key'))
            ) {
              throw new UserFacingError(
                `The name "${name}" is already taken. Please choose a different name.`
              );
            }
            throw updateError;
          }
        } else {
          // Before creating, check if the name is already taken by another user
          const { data: existingName } = await UserService.findByName(name);

          if (existingName && existingName.id !== data.user.id) {
            throw new UserFacingError(
              `The name "${name}" is already taken. Please choose a different name.`
            );
          }

          // Create new user
          const { error: insertError } = await UserService.insertProfile({
            id: data.user.id,
            name,
            email,
            role, // Now using the actual role (including 'driver') since migration is applied
          });

          if (insertError) {
            Logger.serviceError('AuthContext', 'signup', new Error(insertError.message), {
              email,
              name,
              role,
              operation: 'create_user_profile_failed',
              error: insertError.message,
            });

            // Check for duplicate name constraint
            if (
              insertError.message.includes('duplicate key') &&
              (insertError.message.includes('username_unique') ||
                insertError.message.includes('users_name_key'))
            ) {
              throw new UserFacingError(
                `The name "${name}" is already taken. Please choose a different name.`
              );
            }

            // If it's a constraint violation for the role, throw a more specific error
            if (
              insertError.message.includes('violates check constraint') ||
              insertError.message.includes('users_role_check')
            ) {
              throw new UserFacingError(
                `Invalid role '${role}'. Please ensure the database migration for driver role has been applied.`
              );
            }

            // For other database errors, throw them up
            throw new Error(`Database error saving new user: ${insertError.message}`);
          }
        }
      } catch (profileError) {
        if (profileError instanceof UserFacingError) throw profileError;

        // Re-throw if it's already a formatted error
        if (profileError instanceof Error && profileError.message.startsWith('Database error')) {
          throw profileError;
        }

        const errorMessage =
          profileError instanceof Error ? profileError.message : String(profileError);
        Logger.serviceError('AuthContext', 'signup', new Error(errorMessage), {
          email,
          name,
          role,
          operation: 'create_user_profile_failed',
          error: String(profileError),
        });

        // Throw the error instead of continuing
        throw new Error(`Database error saving new user: ${errorMessage}`);
      }

      // Sign out and redirect to login (standard flow for email confirmation)
      await AuthSessionApi.signOut();
      navigate('/auth/login?success=signup');
    } catch (error: unknown) {
      Logger.serviceError('AuthContext', 'signup', error as Error, {
        email,
        name,
        operation: 'signup_error',
      });
      setError(getErrorMessage(error, 'Signup failed'));
    } finally {
      setLoading(false);
    }
  };

  const value: AuthContextType = {
    user,
    loading,
    error,
    session,
    login,
    logout,
    signUp,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
